<template>
  <section style="">
    <form method="post" @submit.prevent="onSend" novalidate>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title is-centered">
            {{ title }}
          </p>
        </header>

        <section class="modal-card-body">
          <form-wrapper :validator="$v.form">
            <div class="columns">
              <div class="column">
                <form-group
                  name="gift_id"
                  attribute="attributes.gift"
                  :label="$t('message.select') + $t('attributes.gift')"
                  label-position="on-border"
                >
                  <b-input
                    v-if="isUpdate"
                    v-model="gift_name"
                    name="gift_name"
                    expanded
                    disabled
                  >
                  </b-input>
                  <b-autocomplete
                    v-else
                    ref="giftSelect"
                    v-model="search"
                    name="gift_id"
                    field="id"
                    :custom-formatter="t => t.full_name"
                    :data="gifts"
                    :loading="loadingGifts"
                    :placeholder="
                      $t('message.please_select') + $t('attributes.gift')
                    "
                    open-on-focus
                    @typing="onSearchGifts"
                    @select="onSelectGift"
                  >
                    <template #default="props">
                      <p>{{ props.option.full_name }}</p>
                    </template>
                    <template #empty>
                      {{
                        form.gift_id
                          ? loadingGifts
                            ? $t('attributes.searching')
                            : $t('attributes.not_found')
                          : $t('message.please_select') + $t('attributes.gift')
                      }}
                    </template>
                  </b-autocomplete>
                </form-group>

                <form-group
                  name="quantity"
                  attribute="attributes.gift_quantity"
                  :label="$t('attributes.gift_quantity')"
                  label-position="on-border"
                >
                  <b-input
                    v-model.number="form.quantity"
                    type="number"
                    name="quantity"
                    min="0"
                    :placeholder="
                      $t('message.please_input') +
                        $t('attributes.gift_quantity')
                    "
                    expanded
                    @input="updateAmount"
                  >
                  </b-input>
                </form-group>

                <form-group
                  name="net_price"
                  attribute="attributes.gift_net_price"
                  :label="$t('attributes.gift_net_price')"
                  label-position="on-border"
                >
                  <b-input
                    v-model.number="form.net_price"
                    type="number"
                    name="net_price"
                    min="0"
                    :disabled="selectedGift && selectedGift.consumable"
                    :placeholder="
                      $t('message.please_input') +
                        $t('attributes.gift_net_price')
                    "
                    expanded
                  >
                  </b-input>
                </form-group>
                <form-group
                  name="cost"
                  attribute="attributes.gift_unit_cost"
                  :label="$t('attributes.gift_unit_cost')"
                  label-position="on-border"
                >
                  <b-input
                    v-model.number="form.cost"
                    type="number"
                    step="0.01"
                    name="cost"
                    min="0"
                    :placeholder="
                      $t('message.please_input') +
                        $t('attributes.gift_unit_cost')
                    "
                    expanded
                    @input="updateAmount"
                  >
                  </b-input>
                </form-group>
                <form-group
                  name="amount"
                  :label="$t('attributes.gift_amount')"
                  label-position="on-border"
                >
                  <b-input
                    v-model.number="form.amount"
                    type="number"
                    step="0.01"
                    name="amount"
                    readonly
                    disabled
                    expanded
                  >
                  </b-input>
                </form-group>
                <form-group
                  name="voucher_number"
                  attribute="attributes.gift_voucher_number"
                  :label="$t('attributes.gift_voucher_number') + '(可後補)'"
                  label-position="on-border"
                >
                  <b-input
                    v-model.number="form.voucher_number"
                    type="voucher_number"
                    name="cost"
                    min="0"
                    placeholder="請輸入科威傳票號碼"
                    expanded
                  >
                  </b-input>
                </form-group>
                <form-group
                  name="voucher_date"
                  attribute="attributes.gift_voucher_date"
                  :label="$t('attributes.gift_voucher_date') + '(可後補)'"
                  label-position="on-border"
                >
                  <b-datepicker
                    v-model="form.voucher_date"
                    placeholder="請選擇傳票日期"
                    :icon-right="form.voucher_date ? 'close-circle' : ''"
                    :max-date="today"
                    icon="calendar-today"
                    icon-right-clickable
                    @icon-right-click="clearDate"
                    trap-focus
                  >
                  </b-datepicker>
                </form-group>
              </div>
              <div class="column">
                <form-group
                  name="date"
                  attribute="attributes.gift_purchase_date"
                  :label="$t('attributes.gift_purchase_date')"
                  label-position="on-border"
                >
                  <b-datepicker
                    v-model="purchaseDate"
                    :placeholder="
                      $t('message.please_input') +
                        $t('attributes.gift_purchase_date')
                    "
                    icon="calendar-today"
                    inline
                    trap-focus
                    @input="onSelectedDate"
                  >
                    <div class="buttons">
                      <b-button
                        :label="$t('attributes.today')"
                        type="is-success"
                        @click="onSelectedToday"
                      />
                      <b-button
                        :label="$t('attributes.yesterday')"
                        type="is-info"
                        @click="onSelectedYesterday"
                      />
                      <b-button
                        :label="$t('attributes.clear')"
                        type="is-danger"
                        @click="onClearDate"
                      />
                    </div>
                  </b-datepicker>
                </form-group>
              </div>
            </div>
          </form-wrapper>
        </section>

        <footer class="modal-card-foot">
          <b-button
            :loading="sending"
            class="is-fullwidth"
            @click="$parent.close()"
          >
            {{ $t('attributes.close') }}
          </b-button>
          <b-button
            :loading="sending"
            type="is-primary"
            class="is-fullwidth"
            native-type="submit"
          >
            {{ isNew ? $t('attributes.add') : $t('attributes.update') }}
          </b-button>
        </footer>

        <b-loading
          :active.sync="isLoading"
          :is-full-page="false"
          :can-cancel="false"
        ></b-loading>
      </div>
    </form>
  </section>
</template>

<script>
import { required, numeric, minValue } from 'vuelidate/lib/validators'
import { GIFTS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import debounce from 'lodash/debounce'

export default {
  name: 'GiftPurchaseForm',

  props: {
    giftPurchase: {
      type: Object,
      required: true
    },
    sending: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      isNew: !this.giftPurchase.id,
      isUpdate: !!this.giftPurchase.id,
      title: !this.giftPurchase.id
        ? this.$t('attributes.add')
        : this.$t('attributes.edit'),

      search: null,
      loadingGifts: false,
      gifts: [],
      selectedGift: null,
      gift_name: this.giftPurchase.gift_name,
      purchaseDate:
        this.giftPurchase.date && this.$dayjs(this.giftPurchase.date).toDate(),
      today: this.getDate(),

      form: {
        gift_id: this.giftPurchase.gift_id,
        date: this.giftPurchase.date,
        quantity: this.giftPurchase.quantity,
        cost: this.giftPurchase.cost,
        net_price: this.giftPurchase.net_price,
        amount: this.giftPurchase.quantity * this.giftPurchase.cost,
        voucher_number: this.giftPurchase.voucher_number,
        voucher_date: this.giftPurchase.voucher_date
      }
    }
  },
  computed: {
    isLoading() {
      return this.loadingGifts || this.sending
    }
  },
  validations: {
    form: {
      gift_id: { required },
      date: { required },
      quantity: { required, numeric, minValue: minValue(1) },
      cost: { required, minValue: minValue(0) },
      net_price: { required, numeric, minValue: minValue(0) },
      voucher_number: {},
      voucher_date: {}
    }
  },
  mounted() {
    if (this.isNew && !this.giftPurchase.gift_id) {
      this.$refs.giftSelect.focus()
    }
    this.loadGiftsAsync(null)
  },
  methods: {
    async loadGiftsAsync(search) {
      try {
        const api = `${GIFTS_API}`
        const params = {
          search: search
        }

        this.loadingGifts = true
        const res = await this.axios.get(api, { params })
        this.gifts = res.data.data
        const gift = this.gifts.find(d => d.id === this.form.gift_id) || null

        if (this.isNew && gift) {
          this.$refs.giftSelect.setSelected(gift)
        }
        this.onSelectGift(gift)
      } catch (e) {
        this.gifts = []
        this.onSelectGift(null)

        notifyService.fetch_error('gift', e)
      } finally {
        this.loadingGifts = false
      }
    },
    onSearchGifts: debounce(function(search) {
      this.loadGiftsAsync(search)
    }, 500),
    onSelectGift(gift) {
      this.selectedGift = gift
      this.form.gift_id = gift && gift.id
      this.form.cost = gift && gift.cost
      this.form.net_price = gift && gift.net_price
      this.updateAmount()
    },
    onSelectedToday() {
      this.purchaseDate = this.$dayjs().toDate()
      this.form.date = this.formatDate(this.purchaseDate)
    },
    onSelectedYesterday() {
      this.purchaseDate = this.$dayjs()
        .subtract(1, 'day')
        .toDate()
      this.form.date = this.formatDate(this.purchaseDate)
    },
    onClearDate() {
      this.purchaseDate = null
      this.form.date = null
    },
    onSelectedDate(date) {
      this.form.date = this.formatDate(date)
    },
    updateAmount() {
      this.form.amount = this.form.cost * this.form.quantity
      this.form.amount = parseFloat(this.form.amount.toFixed(2))
    },
    getDate() {
      let today = new Date()

      let year = today.getFullYear()
      let month = String(today.getMonth() + 1).padStart(2, '0')
      let day = String(today.getDate()).padStart(2, '0')
      let d = `${year}-${month}-${day}`
      return new Date(d)
    },
    clearDate() {
      this.form.voucher_date = null
    },
    async onSend() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }
      if (
        (this.form.voucher_number && !this.form.voucher_date) ||
        (!this.form.voucher_number && this.form.voucher_date)
      ) {
        this.$buefy.dialog.alert({
          title: '錯誤',
          type: 'is-danger',
          message: '有填傳票號碼則必填傳票日期。',
          confirmText: '確定'
        })
        return
      }

      this.$emit('send', this.form)
    }
  }
}
</script>
